import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import UserMenu from './UserMenu';
import './Sidebar.css';

function Sidebar() {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [subMenus, setSubMenus] = useState(() => {
    const saved = localStorage.getItem('sidebarSubMenus');
    return saved ? JSON.parse(saved) : {};
  });

  const toggleCollapsed = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsed));
  };

  const toggleSubMenu = (menu) => {
    const newSubMenus = { ...subMenus, [menu]: !subMenus[menu] };
    setSubMenus(newSubMenus);
    localStorage.setItem('sidebarSubMenus', JSON.stringify(newSubMenus));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const showContent = !collapsed || mobileMenuOpen;

  const isSubMenuActive = (paths) => paths.some(path => location.pathname === path);

  // Helper to render a submenu group
  const renderSubMenu = (key, icon, label, paths, children) => {
    const hasAny = paths.some(p => hasPermission(p));
    if (!hasAny) return null;
    return (
      <div className="nav-submenu-container">
        <div
          className={`nav-submenu-header ${isSubMenuActive(paths) ? 'active' : ''}`}
          onClick={() => {
            if (collapsed && !mobileMenuOpen) {
              setCollapsed(false);
              localStorage.setItem('sidebarCollapsed', 'false');
              // Abrir o submenu ao expandir
              if (!subMenus[key]) {
                const newSubMenus = { ...subMenus, [key]: true };
                setSubMenus(newSubMenus);
                localStorage.setItem('sidebarSubMenus', JSON.stringify(newSubMenus));
              }
            } else {
              toggleSubMenu(key);
            }
          }}
          title={label}
        >
          <span className="nav-icon">{icon}</span>
          {showContent && (
            <>
              <span>{label}</span>
              <span className="submenu-arrow">{subMenus[key] ? '\u25BC' : '\u25B6'}</span>
            </>
          )}
        </div>
        {subMenus[key] && showContent && (
          <div className="nav-submenu-items">
            {children}
          </div>
        )}
      </div>
    );
  };

  const renderItem = (path, icon, label) => {
    if (!hasPermission(path)) return null;
    return (
      <NavLink to={path} className={({ isActive }) => isActive ? 'nav-subitem active' : 'nav-subitem'} onClick={closeMobileMenu} title={label}>
        <span className="nav-icon">{icon}</span>
        <span>{label}</span>
      </NavLink>
    );
  };

  return (
    <>
      <button className="mobile-menu-toggle" onClick={toggleMobileMenu} aria-label="Toggle Menu">
        {mobileMenuOpen ? '\u2715' : '\u2630'}
      </button>
      <div className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`} onClick={closeMobileMenu}></div>

      <div className={`sidebar-dashboard ${mobileMenuOpen ? 'mobile-open' : ''} ${collapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header-dashboard">
          {showContent && (
            <>
              <h2>Laborat&oacute;rio</h2>
              <p>Sistema de Gest&atilde;o</p>
            </>
          )}
          <button className="collapse-toggle" onClick={toggleCollapsed} title={collapsed ? 'Expandir menu' : 'Recolher menu'}>
            {collapsed ? '\u25B6' : '\u25C0'}
          </button>
        </div>

        <nav className="sidebar-nav">
          {/* Dashboard */}
          <NavLink to="/" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'} onClick={closeMobileMenu} title="Dashboard">
            <span className="nav-icon">{'\uD83D\uDCCA'}</span>
            {showContent && <span>Dashboard</span>}
          </NavLink>

          {/* Conversas - acesso rápido */}
          {hasPermission('/whatsapp-conversas') && (
          <NavLink to="/whatsapp-conversas" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'} onClick={closeMobileMenu} title="Conversas WhatsApp">
            <span className="nav-icon">{'\uD83D\uDCAC'}</span>
            {showContent && <span>Conversas</span>}
          </NavLink>
          )}

          {/* Gestão */}
          {renderSubMenu('gestao', '\uD83D\uDCC8', 'Gest\u00e3o', ['/painel-administrativo', '/painel-gestor', '/painel-inconsistencias', '/tempo-exames'], <>
            {renderItem('/painel-administrativo', '\uD83D\uDCCA', 'Painel Administrativo')}
            {renderItem('/painel-gestor', '\uD83C\uDFAF', 'Painel do Gestor')}
            {renderItem('/painel-inconsistencias', '\u26A0\uFE0F', 'Inconsist\u00eancias')}
            {renderItem('/tempo-exames', '\u23F1\uFE0F', 'Tempo de Exames')}
          </>)}

          <div className="nav-separator" />

          {/* Atendimento */}
          {renderSubMenu('atendimento', '\uD83C\uDFE5', 'Atendimento', ['/pacientes', '/solicitacoes', '/atendimentos', '/coletas-domiciliares', '/agendamentos-coleta'], <>
            {renderItem('/pacientes', '\uD83D\uDC65', 'Pacientes')}
            {renderItem('/solicitacoes', '\uD83D\uDCCB', 'Solicita\u00e7\u00f5es')}
            {renderItem('/atendimentos', '\uD83C\uDFE5', 'Atendimentos')}
            {renderItem('/coletas-domiciliares', '\uD83C\uDFE0', 'Coletas')}
            {renderItem('/agendamentos-coleta', '\uD83D\uDCCB', 'Sol. Coleta')}
          </>)}

          {/* Laboratório */}
          {renderSubMenu('laboratorio', '\uD83E\uDDEA', 'Laborat\u00f3rio', ['/resultados', '/laudos', '/lab-apoio', '/orcamentos'], <>
            {renderItem('/resultados', '\uD83D\uDCCB', 'Resultados')}
            {renderItem('/laudos', '\uD83D\uDCC4', 'Laudos')}
            {renderItem('/lab-apoio', '\uD83C\uDFE5', 'Lab. de Apoio')}
            {renderItem('/orcamentos', '\uD83D\uDCB0', 'Or\u00e7amentos')}
          </>)}

          {/* Financeiro */}
          {renderSubMenu('financeiro', '\uD83D\uDCB0', 'Financeiro', ['/faturamento', '/financeiro', '/financeiro/pix-config', '/relatorio-faturamento', '/conferencia-convenios', '/conferencia-hospital', '/tabela-unimed', '/tiss-unimed', '/ipsemg'], <>
            {renderItem('/faturamento', '\uD83D\uDCB3', 'Faturamento')}
            {renderItem('/financeiro', '\uD83C\uDFE6', 'T\u00edtulos e Caixa')}
            {renderItem('/financeiro/pix-config', '\uD83D\uDCB8', 'Chave PIX')}
            {renderItem('/relatorio-faturamento', '\uD83D\uDCCA', 'Relat\u00f3rios')}
            {renderItem('/conferencia-convenios', '\uD83D\uDD0D', 'Confer\u00eancia')}
            {renderItem('/conferencia-hospital', '\uD83C\uDFE5', 'Confer\u00eancia Hospital')}
            {renderItem('/tabela-unimed', '\uD83D\uDCCB', 'Tabela UNIMED')}
            {renderItem('/tiss-unimed', '\uD83C\uDFE5', 'TISS UNIMED')}
            {renderItem('/ipsemg', '\uD83C\uDFE5', 'IPSEMG (TES)')}
          </>)}

          <div className="nav-separator" />

          {/* WhatsApp */}
          {renderSubMenu('whatsapp', '\uD83D\uDCAC', 'WhatsApp', ['/whatsapp-dashboard', '/whatsapp-envios', '/whatsapp-mensagens', '/whatsapp-conversas', '/whatsapp-contatos', '/whatsapp-agente', '/whatsapp-historico', '/whatsapp-diagnostico', '/whatsapp-config'], <>
            {renderItem('/whatsapp-dashboard', '\uD83D\uDCCA', 'Dashboard')}
            {renderItem('/whatsapp-conversas', '\uD83D\uDCAC', 'Conversas')}
            {renderItem('/whatsapp-contatos', '\uD83D\uDCC7', 'Contatos')}
            {renderItem('/whatsapp-envios', '\uD83D\uDCE4', 'Envio de Resultados')}
            {renderItem('/whatsapp-mensagens', '\uD83D\uDCDD', 'Mensagens')}
            {renderItem('/whatsapp-agente', '\uD83E\uDD16', 'Agente IA')}
            {renderItem('/whatsapp-historico', '\uD83D\uDCCB', 'Hist\u00f3rico')}
            {renderItem('/whatsapp-diagnostico', '\uD83D\uDD0D', 'Diagn\u00f3stico')}
            {renderItem('/whatsapp-config', '\u2699\uFE0F', 'Configura\u00e7\u00f5es')}
          </>)}

          <div className="nav-separator" />

          {/* Cadastros (reduzido) */}
          {renderSubMenu('cadastros', '\uD83D\uDCDD', 'Cadastros', ['/exames', '/convenios', '/medicos', '/materiais-metodos'], <>
            {renderItem('/exames', '\uD83E\uDDEA', 'Exames')}
            {renderItem('/convenios', '\uD83C\uDFE6', 'Conv\u00eanios')}
            {renderItem('/medicos', '\uD83D\uDC68\u200D\u2695\uFE0F', 'M\u00e9dicos')}
            {renderItem('/materiais-metodos', '\uD83E\uDDEB', 'Materiais e M\u00e9todos')}
          </>)}

          {/* Infraestrutura */}
          {renderSubMenu('infraestrutura', '\uD83D\uDD27', 'Infraestrutura', ['/aparelhos', '/setores', '/bancadas', '/locais', '/laboratorios', '/recipientes', '/responsaveis'], <>
            {renderItem('/aparelhos', '\u2699\uFE0F', 'Aparelhos')}
            {renderItem('/setores', '\uD83C\uDFE2', 'Setores')}
            {renderItem('/bancadas', '\uD83D\uDD2C', 'Bancadas')}
            {renderItem('/locais', '\uD83D\uDCCD', 'Locais')}
            {renderItem('/laboratorios', '\uD83C\uDFE5', 'Laborat\u00f3rios')}
            {renderItem('/recipientes', '\uD83E\uDDEA', 'Recipientes')}
            {renderItem('/responsaveis', '\uD83D\uDC68\u200D\uD83D\uDD2C', 'Respons\u00e1veis T\u00e9cnicos')}
          </>)}

          {/* Sistema */}
          {renderSubMenu('sistema', '\u2699\uFE0F', 'Sistema', ['/tabelas', '/api-docs', '/perfis', '/usuarios', '/teste-ia'], <>
            {renderItem('/usuarios', '\uD83D\uDC64', 'Usu\u00e1rios')}
            {renderItem('/perfis', '\uD83D\uDEE1\uFE0F', 'Perfis de Acesso')}
            {renderItem('/teste-ia', '\uD83E\uDDEA', 'Teste de IA')}
            {renderItem('/tabelas', '\uD83D\uDDC4\uFE0F', 'Explorador de Tabelas')}
            {renderItem('/api-docs', '\uD83D\uDCDA', 'Documenta\u00e7\u00e3o da API')}
          </>)}
        </nav>

        {user && (
          <div className="sidebar-footer">
            <UserMenu collapsed={collapsed} onLogout={handleLogout} />
          </div>
        )}
      </div>
    </>
  );
}

export default Sidebar;
